<!--
  MedicareCardForm.vue

-->

<template>
  <div>
    <slot name="header"></slot>
    <div class="form-section">
      <FormField
        labelText="Medicare Card type"
        forId="medicareCardTypeOptions.id"
        :error="v$.medicareCard.cardType.$error"
        errorMessage="Please include a response for Medicare Card type."
      >
        <div class="dropdown-select-wrapper">
          <DropdownSelect id="medicareCardType" :items="medicareCardTypeOptionsList" v-model="v$.medicareCard.cardType.$model" />
        </div>
      </FormField>
    </div>

    <div class="form-section">
      <FormField
        labelText="Medicare Card number"
        type="string"
        placeholder="XXXX XXXXX X"
        v-model.trim="v$.medicareCard.cardNumber.$model"
        maxlength="12"
        v-maska="'#### ##### #'"
        :error="v$.medicareCard.cardNumber.$error"
        :errorMessage="calcCardNumberErrorMessage"
      />
    </div>
    <div class="form-section">
      <FormField
        labelText="First name as it appears on your Medicare Card"
        type="string"
        placeholder="e.g. Fred"
        v-model.lazy="v$.medicareCard.firstName.$model"
        :error="v$.medicareCard.firstName.$error"
        :errorMessage="calcFirstNameErrorMessage"
      />
    </div>
    <div class="form-section">
      <FormField
        labelText="Last name as it appears on your Medicare Card"
        type="string"
        placeholder="e.g. Citizen"
        v-model.lazy="v$.medicareCard.lastName.$model"
        :error="v$.medicareCard.lastName.$error"
        :errorMessage="calcLastNameErrorMessage"
      />
    </div>
    <div class="form-section" v-if="isExpiredDateShown">
      <FormField
        labelText="Valid to date on Medicare Card"
        placeholder="DD / MM / YYYY"
        v-maska="'## / ## / ####'"
        type="text"
        maxlength="14"
        v-model.trim="medicareCard.expiryDate"
        :error="v$.medicareCard.expiryDate.$error"
        :errorMessage="calcExpiryDateErrorMessage"
        class="half-width"
        @input="v$.medicareCard.expiryDate.$touch"
      />
    </div>
  </div>
</template>
<script>
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf, minLength, maxLength } from '@vuelidate/validators';
import { MedicareCardType } from '@general/js/enums';
import { inputWithNumbersAndSpaces, isPastDateExcludingToday } from '@general/js/utils';
import { JOIN_NOW_NAME_VALIDATOR, MEDICARE_CARD_CHECKSUM_VALIDATOR, MEDICARE_CARD_EXPIRY_DATE_PATTERN_CHECK } from '@general/js/validators';
import DropdownSelect from '@general/js/components/DropdownSelect.vue';
import FormField from './FormField.vue';

export default {
  setup() { return { v$: useVuelidate() }; },

  name: 'MedicareCardForm',

  components: {
    DropdownSelect,
    FormField,
  },

  props: {
    medicareCardData: {
      type: Object,
    },

    disabledCardTypeInput: {
      type: Boolean,
      default: false,
    },
  },

  beforeMount() {
    this.resetData();

    if (this.medicareCardData) {
      if (this.medicareCardData.cardType) {
        this.medicareCard.cardType = this.medicareCardData.cardType;
        this.v$.medicareCard.cardType.$touch();
      }
      if (this.medicareCardData.cardNumber) {
        this.medicareCard.cardNumber = this.medicareCardData.cardNumber;
        this.v$.medicareCard.cardNumber.$touch();
      }
      if (this.medicareCardData.firstName) {
        this.medicareCard.firstName = this.medicareCardData.firstName;
        this.v$.medicareCard.firstName.$touch();
      }
      if (this.medicareCardData.lastName) {
        this.medicareCard.lastName = this.medicareCardData.lastName;
        this.v$.medicareCard.lastName.$touch();
      }
      if (this.medicareCardData.expiryDate) {
        this.medicareCard.expiryDate = this.medicareCardData.expiryDate;
        this.v$.medicareCard.expiryDate.$touch();
      }
    }
  },

  data() {
    return {
      medicareCard: {
        cardType: null,
        cardNumber: null,
        firstName: '',
        lastName: '',
        expiryDate: null,
      },
      medicareCardTypeOptions: [
        {
          display: 'Green',
          returnValue: 'Green',
        },
        {
          display: 'Blue (Interim)',
          returnValue: 'Blue (Interim)',
        },
        {
          display: 'Yellow (Reciprocal)',
          returnValue: 'Yellow (Reciprocal)',
        },
      ],
    };
  },

  watch: {
    medicareCard: {
      deep: true,
      handler() {
        this.$emit('updateMedicareCard', {
          isFormValid: !this.v$.$invalid,
          formData: this.medicareCard,
        });
      },
    },
    'medicareCard.cardType': function () {
      if (!this.isExpiredDateShown) {
        this.medicareCard.expiryDate = null;
      }
    },
  },

  methods: {
    resetData() {
      this.medicareCard = {
        cardType: null,
        cardNumber: null,
        firstName: '',
        lastName: '',
        expiryDate: null,
      };
    },
  },

  computed: {
    getDropDownLabel() {
      if (this.medicareCard.cardType === null) {
        return 'Please select a Medicare Card type';
      }
      return this.medicareCard.cardType;
    },
    isExpiredDateShown() {
      if (this.medicareCard.cardType !== null) {
        // expiry date is not shown for Green medicare cards
        return this.medicareCard.cardType !== MedicareCardType.GREEN && this.medicareCard.cardType !== 'G'; // need to use && because it's !==
      }
      return false;
    },
    calcCardNumberErrorMessage() {
      if (this.v$.medicareCard.cardNumber.required.$invalid) {
        return 'Please include a response for Medicare Card number.';
      }
      if (this.v$.medicareCard.cardNumber.inputWithNumbersAndSpaces.$invalid) {
        return "We can't accept that input - please remove letters, symbols and/or consecutive spaces.";
      }
      return 'Please check your Medicare Card number.';
    },
    calcFirstNameErrorMessage() {
      if (this.v$.medicareCard.firstName.required.$invalid) {
        return 'Please include a response for First name.';
      }
      if (this.v$.medicareCard.firstName.maxLength.$invalid) {
        return 'You have exceeded the maximum amount of characters.';
      }
      return "We can't accept that input - please remove numbers, symbols and/or consecutive spaces.";
    },
    calcLastNameErrorMessage() {
      if (this.v$.medicareCard.lastName.required.$invalid) {
        return 'Please include a response for Last name.';
      }
      if (this.v$.medicareCard.lastName.maxLength.$invalid) {
        return 'You have exceeded the maximum amount of characters.';
      }
      return "We can't accept that input - please remove numbers, symbols and/or consecutive spaces.";
    },
    calcExpiryDateErrorMessage() {
      if (this.isExpiredDateShown) {
        if (this.v$.medicareCard.expiryDate.required.$invalid) {
          return 'Please include a response for valid Medicare Card date.';
        }
        if (this.v$.medicareCard.expiryDate.MEDICARE_CARD_EXPIRY_DATE_PATTERN_CHECK.$invalid) {
          return "We can't accept that input - please remove letters, symbols and/or consecutive spaces.";
        }
        if (this.v$.medicareCard.expiryDate.isPastDateExcludingToday.$invalid) {
          return 'Past months are not accepted. Please try again.';
        }
      }
      return '';
    },
    medicareCardTypeOptionsList() {
      return this.medicareCardTypeOptions.map((option) => {
        return { name: option.display, value: option.returnValue };
      });
    },
  },

  validations() {
    return {
      medicareCard: {
        cardType: {
          required,
        },
        cardNumber: {
          required,
          minLength: minLength(12),
          maxLength: maxLength(12),
          MEDICARE_CARD_CHECKSUM_VALIDATOR(cardNumber) {
            return MEDICARE_CARD_CHECKSUM_VALIDATOR(cardNumber);
          },
          inputWithNumbersAndSpaces(cardNumber) {
            return inputWithNumbersAndSpaces(cardNumber);
          },
        },
        firstName: {
          required,
          JOIN_NOW_NAME_VALIDATOR,
          maxLength: maxLength(160),
        },
        lastName: {
          required,
          JOIN_NOW_NAME_VALIDATOR,
          maxLength: maxLength(160),
        },
        expiryDate: {
          required: requiredIf(() => this.isExpiredDateShown),
          MEDICARE_CARD_EXPIRY_DATE_PATTERN_CHECK: (value) => {
            if (!this.isExpiredDateShown || value == null) return true;
            return MEDICARE_CARD_EXPIRY_DATE_PATTERN_CHECK.$validator ? MEDICARE_CARD_EXPIRY_DATE_PATTERN_CHECK.$validator(value) : MEDICARE_CARD_EXPIRY_DATE_PATTERN_CHECK(value);
          },
          isPastDateExcludingToday: (value) => {
            if (!this.isExpiredDateShown || value == null) return true;
            return !isPastDateExcludingToday(value);
          },
        },
      },
    };
  },
};
</script>
<style lang="scss" scoped>
.dropdown-select-wrapper {
  overflow: visible;
}
</style>
